import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
} from "@material-ui/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import DropdownComponent from "../../Components/Dropdown/DropdownComponent";
import { MergeLinkCard } from "../../Components/MergeLink/MergeLink";
import DeleteBaseClientModal from "../../Components/Modal/DeleteBaseClientModal";
import Breadcrumb from "../../Components/Navigation/Breadcrumbs/Breadcrumbs";
import Signifier from "../../Components/Signifier";
import { StyledTextField } from "../../Components/TextField/TextField";
import { BillingTier } from "../../Enums/BillingTier";
import { MAX_URL_LENGTH, validateHostname, validateUrl } from "../../Helpers/UrlValidator";
import { useClientBillingTiers, useGetBillingTierByName } from "../../Helpers/billingTierDefaultPricing";
import { validateEmail } from "../../Helpers/emailValidator";
import { useCrmClientInfoQuery } from "../../Queries/BaseClientManagementQueries";
import { editClient, getClient, migrateClient } from "../../Services/API/BaseClientManagement";
import { selectUserInformation } from "../../State/Slices/UserInformationSlice";
import { BaseClient, EditBaseClient, IClinicalAnalyticsConfig } from "../../Types/BaseClientManagement";
import { ClientBillingTier } from "../../Types/ClientBilling";
import ClinicalTrialConfigCard from "./components/ClinicalTrialConfig/ClinicalTrialConfigCard";
import { useBaseClientManagementStyles } from "./styles/BaseClientManagementStyles";

export default function BaseClientDetailsPage() {
  const classes = useBaseClientManagementStyles();
  const { "http://nasrecruitment.io/activateId": activateId } = useSelector(selectUserInformation);
  const { tiers } = useClientBillingTiers();
  const location = useLocation<BaseClient>();
  const history = useHistory();

  const id = location.state.id || "";
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [client, setClient] = useState({});
  const [clientName, setClientName] = useState(location.state.clientName ?? "");
  const [termsOfServiceUrl, setTermsOfServiceUrl] = useState(location.state.termsOfServiceUrl ?? "");
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState(location.state.privacyPolicyUrl ?? "");
  const [privacyPolicyTouched, setPrivacyPolicyTouched] = useState(false);
  const [hasAnalytics, setHasAnalytics] = useState(location.state.hasAnalytics ?? false);
  const [hasCareers, setHasCareers] = useState(location.state.hasCareers ?? false);
  const [hasClinicalAnalytics, setHasClinicalAnalytics] = useState(
    location.state.hasClinicalAnalytics ?? false,
  );
  const [hasCrm, setHasCRM] = useState(location.state.hasCrm ?? false);
  const [isActivate4, setIsActivate4] = useState(location.state.isActivate4 ?? false);
  const [tierName, setTierName] = useState((location.state.tierName as BillingTier) ?? BillingTier.Initiate);
  const tierData = useGetBillingTierByName(tierName) ?? ({} as ClientBillingTier);
  const [monthlyBasePrice, setMonthlyBasePrice] = useState(
    location.state.monthlyBasePrice ?? tierData.monthlyBasePrice,
  );
  const [emailQuota, setEmailQuota] = useState(location.state.emailQuota ?? tierData.emailAllowance);
  const [emailRate, setEmailRate] = useState(location.state.emailRate ?? tierData.emailRate);
  const [textQuota, setTextQuota] = useState(location.state.textQuota ?? tierData.textAllowance);
  const [textRate, setTextRate] = useState(location.state.textRate ?? tierData.textRate);
  const [analyticsCode, setAnalyticsCode] = useState(location.state.analyticsCode ?? "");
  const [analyticsG4PropertyID, setAnalyticsG4PropertyID] = useState(
    location.state.analyticsG4PropertyID ?? "",
  );
  const [emailDomain, setEmailDomain] = useState(location.state.emailDomain ?? "");
  const [replyToDomain, setReplyToDomain] = useState(location.state.replyToDomain ?? "");
  const [shortUrlDomain, setShortUrlDomain] = useState(location.state.shortUrlDomain ?? "");
  const [showSignifier, setShowSignifier] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [showInputError, setShowInputError] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [successfulDelete, setSuccessfulDelete] = useState(false);
  const [refreshClientData, setRefreshClientData] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [accountDirectorName, setAccountDirectorName] = useState(location.state.accountDirectorName ?? "");
  const [accountDirectorEmail, setAccountDirectorEmail] = useState(location.state.accountDirectorEmail ?? "");
  const { data: crmClientData } = useCrmClientInfoQuery(id);

  const clinicalAnalyticsConfigRef = useRef<IClinicalAnalyticsConfig>({
    dialpadApiKey: "",
    facebookAdAccountId: "",
    facebookApiKey: "",
    qualtricsApiToken: "",
    quickbaseApiToken: "",
    quickbaseRealmHostName: "",
  });

  useEffect(() => {
    const fetchClient = async () => {
      const client = await getClient(id);
      setClient(client);
      setRefreshClientData(false);
    };
    if (!!id && refreshClientData) {
      fetchClient();
    }
  }, [id, refreshClientData]);

  useEffect(() => {
    if (location.state === null || location.state === undefined) {
      history.replace("/baseclientmanagement");
    }
  }, [location.state, history]);

  const migrateClientJson = (clientData: EditBaseClient, responseMessage: string) => {
    migrateClient(clientData).then((res) => {
      setShowInputError(false);
      setShowSignifier(true);
      if (res) {
        setResponseMessage(responseMessage);
        setShowError(false);
      } else {
        setShowError(true);
      }
    });
  };

  function isInputValid() {
    return (
      clientName.trim().length > 0 &&
      emailQuota >= 0 &&
      textQuota >= 0 &&
      validateHostname(emailDomain) &&
      validateHostname(replyToDomain) &&
      validateHostname(shortUrlDomain) &&
      (!accountDirectorEmail || validateEmail(accountDirectorEmail)) &&
      validateUrl(privacyPolicyUrl) &&
      (!termsOfServiceUrl || validateUrl(termsOfServiceUrl))
    );
  }

  const saveClientJson = (doMigration: boolean) => {
    //TODO this should be a more robust validation method, but maybe it can wait till a Client Creation Wizard Revamp?
    if (!submissionAttempted) {
      setSubmissionAttempted(true);
    }

    if (!isInputValid()) {
      setShowErrorModal(true);
      setShowInputError(clientName.trim().length === 0);
      return;
    }

    const json = getClientJson();
    submitData(json, "Client Saved", doMigration);
  };

  const getClientJson = useCallback(() => {
    return {
      client: {
        id,
        clientName,
        clinicalAnalyticsConfig: clinicalAnalyticsConfigRef.current,
        termsOfServiceUrl,
        privacyPolicyUrl,
        hasAnalytics,
        hasCareers,
        hasClinicalAnalytics,
        hasCrm,
        isActivate4,
        monthlyBasePrice,
        emailQuota,
        emailRate,
        textQuota,
        textRate,
        tierName,
        analyticsCode,
        analyticsG4PropertyID,
        emailDomain,
        replyToDomain,
        shortUrlDomain,
        userId: activateId,
        accountDirectorEmail,
        accountDirectorName,
      },
    } as EditBaseClient;
  }, [
    accountDirectorEmail,
    accountDirectorName,
    activateId,
    analyticsCode,
    analyticsG4PropertyID,
    clientName,
    emailQuota,
    emailRate,
    hasAnalytics,
    hasCareers,
    hasClinicalAnalytics,
    hasCrm,
    id,
    isActivate4,
    monthlyBasePrice,
    privacyPolicyUrl,
    emailDomain,
    replyToDomain,
    shortUrlDomain,
    termsOfServiceUrl,
    textQuota,
    textRate,
    tierName,
  ]);

  function changeBillingTier(tier: BillingTier) {
    setTierName(() => tier);
    const billingTier = tiers.find((t) => t.tierName === tier);
    if (billingTier) {
      setMonthlyBasePrice(billingTier.monthlyBasePrice);
      setEmailRate(billingTier.emailRate);
      setEmailQuota(billingTier.emailAllowance);
      setTextRate(billingTier.textRate);
      setTextQuota(billingTier.textAllowance);
    }
  }

  const submitData = (clientData: EditBaseClient, responseMessage: string, doMigration: boolean) => {
    editClient(clientData).then((res) => {
      setShowSignifier(true);
      if (res) {
        setResponseMessage(responseMessage);
        setShowError(false);
        if (doMigration) {
          migrateClientJson(clientData, "Client Migrated");
        }
      } else {
        setShowError(true);
      }
    });
  };

  return (
    <>
      <Signifier
        showSignifier={showSignifier}
        setShowSignifier={setShowSignifier}
        message={responseMessage}
        error={showError}
        errorMessage={undefined}
      />
      <div className="page-container">
        <Breadcrumb />
        <h1 className="page-title">Edit Client</h1>
        <div className={classes.root}>
          <div className={classes.header}>
            <div className={classes.descBlock}>
              <p className={classes.descLabel}>DATE CREATED: </p>
              <p className={classes.descValue}>{location.state.dateCreated}</p>
            </div>
            <div className={classes.descBlock}>
              <p className={classes.descLabel}>DATE LAST UPDATED: </p>
              <p className={classes.descValue}>{location.state.dateLastUpdated}</p>
            </div>
          </div>

          <Grid>
            <Card className={classes.card}>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <FormHelperText>Client Details</FormHelperText>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <StyledTextField
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      placeholder="Client Name"
                      label="Client Name"
                      variant="standard"
                      fullWidth
                      required
                      error={showInputError && clientName === ""}
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      data-test="edit-base-client-name-input"
                    />
                    {showInputError && clientName === "" ? (
                      <FormHelperText className={classes.error}>Client Name Required</FormHelperText>
                    ) : null}
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <StyledTextField
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      placeholder="Terms Of Service URL"
                      label="Terms Of Service URL"
                      variant="standard"
                      fullWidth
                      value={termsOfServiceUrl}
                      onChange={(e) => setTermsOfServiceUrl(e.target.value)}
                      error={!!termsOfServiceUrl && !validateUrl(termsOfServiceUrl)}
                    />
                    {!!termsOfServiceUrl && !validateUrl(termsOfServiceUrl) && (
                      <FormHelperText className={classes.error}>Invalid URL</FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <StyledTextField
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      placeholder="Privacy Policy URL"
                      label="Privacy Policy URL"
                      variant="standard"
                      fullWidth
                      value={privacyPolicyUrl}
                      onChange={(e) => {
                        if (!privacyPolicyTouched) {
                          setPrivacyPolicyTouched(true);
                        }
                        setPrivacyPolicyUrl(e.target.value);
                      }}
                      required={true}
                      error={(submissionAttempted || privacyPolicyTouched) && !validateUrl(privacyPolicyUrl)}
                    />
                    {(submissionAttempted || privacyPolicyTouched) && !validateUrl(privacyPolicyUrl) && (
                      <FormHelperText className={classes.error}>Invalid URL</FormHelperText>
                    )}
                  </Grid>
                </Grid>
                <Grid container spacing={1}>
                  <Grid item xs={12} style={{ paddingTop: 40 }}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <FormHelperText>Account Director Information</FormHelperText>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <StyledTextField
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      placeholder="Name"
                      label="Name"
                      variant="standard"
                      fullWidth
                      value={accountDirectorName}
                      onChange={(e) => setAccountDirectorName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <StyledTextField
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      placeholder="Email"
                      label="Email"
                      variant="standard"
                      fullWidth
                      error={!!accountDirectorEmail && !validateEmail(accountDirectorEmail)}
                      value={accountDirectorEmail}
                      onChange={(e) => setAccountDirectorEmail(e.target.value)}
                    />
                    {accountDirectorEmail && !validateEmail(accountDirectorEmail) ? (
                      <FormHelperText className={classes.error}>Invalid email address</FormHelperText>
                    ) : null}
                  </Grid>
                </Grid>
                <Grid container spacing={1}>
                  <Grid item xs={12} style={{ paddingTop: 40 }}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <FormHelperText>Client Products</FormHelperText>
                  </Grid>
                  <Grid container item xs={12} spacing={1}>
                    <Grid item xs={12} md={6} lg={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hasAnalytics}
                            color="primary"
                            data-test="base-client-analytics-check"
                            onChange={(e) => setHasAnalytics(e.target.checked)}
                          />
                        }
                        label="Analytics"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hasCareers}
                            color="primary"
                            data-test="base-client-careers-check"
                            onChange={(e) => setHasCareers(e.target.checked)}
                          />
                        }
                        label="Careers"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hasClinicalAnalytics}
                            color="primary"
                            data-test="base-client-clinicalanalytics-check"
                            onChange={(e) => setHasClinicalAnalytics(e.target.checked)}
                          />
                        }
                        label="Clinical Analytics"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hasCrm}
                            color="primary"
                            data-test="base-client-crm-check"
                            onChange={(e) => setHasCRM(e.target.checked)}
                          />
                        }
                        label="CRM"
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isActivate4}
                            color="primary"
                            data-test="base-client-activate4-check"
                            onChange={(e) => setIsActivate4(e.target.checked)}
                          />
                        }
                        label="Is Activate 4 Client"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container spacing={1}>
                  <Grid item xs={12} style={{ paddingTop: 20 }}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <FormHelperText>Billing Tier</FormHelperText>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DropdownComponent
                      currValue={tierName}
                      items={tiers}
                      handleChange={(name: string) =>
                        changeBillingTier(
                          tiers.find((t) => t.tierName === name)?.tierName ?? BillingTier.Initiate,
                        )
                      }
                      property="tierName"
                      title="Tier"
                      data-test={"editclient-tier-dropdown"}
                      error={emailQuota < 0}
                      multi={false}
                      itemIdSelector={(item: ClientBillingTier) => item.tierName}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      style={{ width: "100%" }}
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      error={monthlyBasePrice < 0}
                      type="number"
                      label="Monthly Price"
                      value={monthlyBasePrice}
                      variant="standard"
                      onChange={(e) => setMonthlyBasePrice(Number(e.target.value))}
                      data-test="edit-base-client-monthlyprice-input"
                      inputProps={{
                        min: 0,
                        step: 500,
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                    {monthlyBasePrice < 0 && (
                      <FormHelperText className={classes.error}>
                        Negative numbers are not allowed
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      style={{ width: "100%" }}
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      error={emailQuota < 0}
                      type="number"
                      label="Email Quota"
                      value={emailQuota}
                      variant="standard"
                      onChange={(e) => setEmailQuota(Number(e.target.value))}
                      data-test="edit-base-client-emailquotas-input"
                      inputProps={{
                        min: 0,
                        step: 1000,
                      }}
                    />
                    {emailQuota < 0 && (
                      <FormHelperText className={classes.error}>
                        Negative numbers are not allowed
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      style={{ width: "100%" }}
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      error={emailRate < 0}
                      type="number"
                      label="Email Overage Rate"
                      value={emailRate}
                      variant="standard"
                      onChange={(e) => setEmailRate(Number(e.target.value))}
                      data-test="edit-base-client-emailrate-input"
                      inputProps={{
                        min: 0.0,
                        step: 0.01,
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                    {emailRate < 0 && (
                      <FormHelperText className={classes.error}>
                        Negative numbers are not allowed
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      style={{ width: "100%" }}
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      error={textQuota < 0}
                      type="number"
                      label="Texting Quota"
                      value={textQuota}
                      variant="standard"
                      onChange={(e) => setTextQuota(Number(e.target.value))}
                      data-test="edit-base-client-textquotas-input"
                      inputProps={{
                        min: 0,
                        step: 1000,
                      }}
                    />
                    {textQuota < 0 && (
                      <FormHelperText className={classes.error}>
                        Negative numbers are not allowed
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      style={{ width: "100%" }}
                      InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                      error={textRate < 0}
                      type="number"
                      label="Text Overage Rate"
                      value={textRate}
                      variant="standard"
                      onChange={(e) => setTextRate(Number(e.target.value))}
                      data-test="edit-base-client-textrate-input"
                      inputProps={{
                        min: 0.0,
                        step: 0.01,
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                    {textRate < 0 && (
                      <FormHelperText className={classes.error}>
                        Negative numbers are not allowed
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>
                {hasAnalytics ? (
                  <Grid container spacing={1}>
                    <Grid item xs={12} style={{ paddingTop: 20 }}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <FormHelperText>Analytics Setup</FormHelperText>
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        style={{ width: "100%", marginTop: "2%" }}
                        multiline
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        label="Analytics Code"
                        value={analyticsCode}
                        variant="standard"
                        onChange={(e) => setAnalyticsCode(e.target.value)}
                        data-test="edit-base-client-analyticscode-input"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        style={{ width: "100%", marginTop: "2%" }}
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        label="G4 Property ID"
                        value={analyticsG4PropertyID}
                        variant="standard"
                        onChange={(e) => setAnalyticsG4PropertyID(e.target.value)}
                        data-test="edit-base-client-analyticsg4-input"
                      />
                    </Grid>
                  </Grid>
                ) : null}
                <Grid container spacing={1}>
                  <Grid item xs={12} style={{ paddingTop: 20 }}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <FormHelperText>Integration Settings</FormHelperText>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={3}>
                      <StyledTextField
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        className={classes.textField}
                        type="text"
                        label="Short IO Domain"
                        value={shortUrlDomain}
                        variant="standard"
                        onChange={(e) => setShortUrlDomain(e.target.value)}
                        data-test="edit-base-client-shortio-domain"
                        error={!validateHostname(shortUrlDomain)}
                      />
                      {!validateHostname(shortUrlDomain) && (
                        <FormHelperText className={classes.error}>Domain is not valid</FormHelperText>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StyledTextField
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        type="text"
                        label="Twilio"
                        value={
                          crmClientData && crmClientData.twilioPhone
                            ? crmClientData?.twilioPhone
                            : "No phone number found"
                        }
                        variant="standard"
                        data-test="twilio-phone-number"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StyledTextField
                        InputLabelProps={{
                          style: { color: "rgba(0,0,0,.6)" },
                          title:
                            "Configure the email domain you want to use when sending emails from this client",
                        }}
                        className={classes.textField}
                        type="text"
                        label="CRM Sender Domain"
                        placeholder="careers.client-name.com"
                        value={emailDomain}
                        variant="standard"
                        onChange={(e) => setEmailDomain(e.target.value)}
                        data-test="edit-base-client-email-domain"
                        error={!validateHostname(emailDomain)}
                      />
                      {!validateHostname(emailDomain) && (
                        <FormHelperText className={classes.error}>
                          Domain is not valid
                          {emailDomain.length > MAX_URL_LENGTH
                            ? ". Must be less than or equal 150 characters"
                            : ""}
                        </FormHelperText>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <StyledTextField
                        InputLabelProps={{
                          style: { color: "rgba(0,0,0,.6)" },
                          title:
                            "Configure the email domain you want to use when receiving emails from CRM candidate replies",
                        }}
                        className={classes.textField}
                        type="text"
                        label="CRM Reply-To Domain"
                        placeholder="careers.client-name.com"
                        value={replyToDomain}
                        variant="standard"
                        onChange={(e) => setReplyToDomain(e.target.value)}
                        data-test="edit-base-client-reply-to-domain"
                        error={!validateHostname(replyToDomain)}
                      />
                      {!validateHostname(replyToDomain) && (
                        <FormHelperText className={classes.error}>
                          Domain is not valid
                          {replyToDomain.length > MAX_URL_LENGTH
                            ? ". Must be less than or equal 150 characters"
                            : ""}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container style={{ justifyContent: "flex-end" }}>
                  {import.meta.env.VITE_NODE_ENV === "staging" ||
                  import.meta.env.VITE_NODE_ENV === "production" ? (
                    <Button
                      style={{ marginLeft: "20px" }}
                      className={classes.sensitiveButton}
                      type="submit"
                      variant="contained"
                      color="primary"
                      onClick={() => saveClientJson(true)}
                      data-test="edit-base-client-migrate-button"
                    >
                      Migrate Client
                    </Button>
                  ) : null}
                  <Button
                    style={{
                      marginLeft: "20px",
                    }}
                    className={classes.sensitiveButton}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={() => saveClientJson(false)}
                    data-test="edit-base-client-name-save-button"
                  >
                    Save Information
                  </Button>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {hasClinicalAnalytics && (
            <Grid item>
              <ClinicalTrialConfigCard
                client={client as BaseClient}
                clinicalAnalyticsConfigRef={clinicalAnalyticsConfigRef}
                handleSaveClient={saveClientJson}
              />
            </Grid>
          )}
          <Grid item>
            <MergeLinkCard client={client} />
          </Grid>
          <Grid xs={12} container item>
            <Button
              className={classes.deleteButton}
              type="submit"
              variant="contained"
              color="secondary"
              onClick={() => setDeleteModal(true)}
              data-test="edit-base-client-name-delete-button"
            >
              Delete Client
            </Button>
          </Grid>
        </div>
      </div>
      <Dialog
        open={deleteModal}
        onClose={(event, reason) => {
          if (!successfulDelete && !deleteProcessing && reason === "backdropClick") {
            setDeleteModal(false);
          } else if (successfulDelete && reason === "backdropClick") {
            return;
          }
        }}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <DeleteBaseClientModal
          clientId={location.state.id}
          clientName={location.state.clientName}
          deleteProcessing={deleteProcessing}
          setDeleteProcessing={setDeleteProcessing}
          successfulDelete={successfulDelete}
          setSuccessfulDelete={setSuccessfulDelete}
          closeModal={() => setDeleteModal(false)}
        />
      </Dialog>
      <Dialog open={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <DialogContent>Please correct fields with errors before saving.</DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorModal(false)} type="submit" color="primary" variant="contained">
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
